import React from 'react'

import { DateTime } from 'luxon'

import { PERIOD_OPTIONS } from '@/components/charts/history/periodOptions'
import type { HistoryWindow } from '@/components/charts/history/useHistoryNavigation'
import { Alert } from '@/components/ui'
import { useApi, useDashboard } from '@/hooks'
import useCompany from '@/hooks/useCompany'
import { dataSourceParam } from '@/insights/common/dataSourceParam'
import { buildQuery } from '@/insights/common/queryUtils'
import { getUserTimezone } from '@/insights/common/timezone'
import Caption, { addDateToCaptionFields } from '@/insights/components/Caption'
import {
  type ApiService,
  type Asset,
  AssetTypeFilter,
  type InsightConfigControl,
  type InsightDefinition,
  type InsightParametersType,
  InsightStatusCode,
  isInsightStatus,
} from '@/types/dashboard-types'

import AssetHistoryChart from './AssetHistoryChart'
import type { AssetHistory, TempUnits, TemperatureLimits } from './types'

export interface HistoryGraphParams extends InsightParametersType {
  companyId?: number
  assetId?: string
  periodHours?: string
  date: string
  from?: string
}

/**
 * Compute the window start for a named date, in the user's profile timezone.
 * Always returns a zone-qualified ISO 8601 string (with offset) so the value
 * round-trips identically whether it's freshly computed or restored from a
 * persisted config — the backend and chart then agree on the same instant.
 * - "today": starts at 00:00 of the current calendar day (window is fixed at 24h)
 * - "yesterday": starts at 00:00 of yesterday (window is fixed at 24h)
 * - "last24Hours": rolling window ending at the current time rounded up to the next hour
 * - "thisWeek" / "lastWeek" / "thisMonth" / "previous1Month": starts at the beginning of that period
 * - "custom": interprets the user's chosen date in the profile timezone
 */
function computeWindowStart(date: string, fromISO: string | undefined, periodHours: number, zone: string): string {
  const now = DateTime.now().setZone(zone)

  if (date === 'custom' && fromISO) {
    // A zoneless wall-clock (no offset/Z) is the literal the user picked or
    // navigated to. The SERVER applies the profile timezone to it, and the chart
    // renders in that same zone — that's what makes the picked digits (e.g.
    // 00:00) survive a save/reload and even a timezone change. Keep it verbatim.
    if (typeof fromISO === 'string' && !/([zZ]|[+-]\d\d:?\d\d)$/.test(fromISO)) {
      return fromISO
    }
    // Otherwise it's an absolute instant. The only way one reaches here is a
    // persisted value that some layer bound to the BROWSER zone before storing
    // (e.g. `new Date(literal).toISOString()`), turning "00:00" into a UTC
    // instant. Render it back in the BROWSER zone to recover the original
    // wall-clock digits, then emit zoneless so the server re-applies the profile
    // zone. (Navigation feeds zoneless wall-clocks, so it never lands here.)
    const dt = typeof fromISO === 'string' ? DateTime.fromISO(fromISO) : DateTime.fromJSDate(fromISO as Date)
    return (dt.isValid ? dt : now).toFormat("yyyy-MM-dd'T'HH:mm:ss")
  }
  if (date === 'thisWeek') {
    return now.startOf('week').toISO()!
  }
  if (date === 'lastWeek') {
    return now.minus({ weeks: 1 }).startOf('week').toISO()!
  }
  if (date === 'thisMonth') {
    return now.startOf('month').toISO()!
  }
  if (date === 'previous1Month') {
    return now.minus({ months: 1 }).startOf('month').toISO()!
  }
  if (date === 'yesterday') {
    return now.minus({ days: 1 }).startOf('day').toISO()!
  }
  if (date === 'last24Hours') {
    return now.plus({ hours: 1 }).startOf('hour').minus({ hours: periodHours }).toISO()!
  }
  // 'today' or unspecified
  return now.startOf('day').toISO()!
}

/** Date options that always span exactly one calendar day (24h), regardless of periodHours selection. */
const FIXED_DAY_OPTIONS = new Set(['today', 'yesterday'])

export const load = async (api: ApiService, parameters: HistoryGraphParams) => {
  const { assetId, ...rest } = parameters
  const periodHours = FIXED_DAY_OPTIONS.has(rest.date) ? 24 : parseInt(rest.periodHours || '24', 10) || 24
  const windowStart = computeWindowStart(rest.date, rest.from, periodHours, getUserTimezone())
  // includeAlertPins is opt-in server-side; the string form is required because
  // buildQuery serialises booleans to ''.
  const query = buildQuery({
    ...rest,
    date: 'custom',
    from: windowStart,
    periodHours: String(periodHours),
    includeAlertPins: 'true',
  })

  const path = `/assets/history/${assetId}?${query}`
  const startedAt = Date.now()
  // [diag blank-on-reconfigure] trace each load call so we can correlate a blank chart
  // with whether the request reached the server, was aborted, or returned an error.
  console.debug('[HistoryGraph.load] →', path)
  try {
    const response = await api.simpleGet<AssetHistory>({
      path,
      origin: 'HistoryGraph',
    })
    console.debug('[HistoryGraph.load] ← ok', path, `${Date.now() - startedAt}ms`)
    return response
  } catch (ex) {
    console.error('[HistoryGraph.load] ← err', path, `${Date.now() - startedAt}ms`, ex)
    return { code: InsightStatusCode.ERROR, message: (ex as Error).message }
  }
}

const loadTemperatureLimits = async (
  api: ApiService,
  parameters: HistoryGraphParams
): Promise<TemperatureLimits | undefined> => {
  const { companyId, assetId } = parameters
  const path = `/assets?companyId=${companyId}&id=${assetId}&deleted=false${dataSourceParam(parameters)}`
  try {
    const response = await api.get<Asset[]>({ path, origin: 'HistoryGraph-limits' })
    const asset = response.data?.[0]
    return asset ? { upper: asset.temperatureUpperLimit, lower: asset.temperatureLowerLimit } : undefined
  } catch (ex) {
    console.error('[HistoryGraph.loadTemperatureLimits] ← err', path, ex)
    return undefined
  }
}

export const loadWithTemperatureLimits = async (api: ApiService, parameters: HistoryGraphParams) => {
  const [history, temperatureLimits] = await Promise.all([
    load(api, parameters),
    loadTemperatureLimits(api, parameters),
  ])
  if (isInsightStatus(history)) {
    return history
  }
  return { ...history, temperatureLimits }
}

export const DateOptions = [
  { label: 'Today', value: 'today' },
  { label: 'Yesterday', value: 'yesterday' },
  { label: 'Last 24 hours', value: 'last24Hours' },
  { label: 'Start of This Week', value: 'thisWeek' },
  { label: 'Start of Last Week', value: 'lastWeek' },
  { label: 'Start of This Month', value: 'thisMonth' },
  { label: 'Start of Last Month', value: 'previous1Month' },
  { label: 'Custom', value: 'custom' },
]

export const buildHistoryGraphControls = (
  assetType: AssetTypeFilter,
  assetLabel?: string
): InsightConfigControl<HistoryGraphParams>[] => [
  { id: 'companyId', control: 'CompanyDropdown', required: true },
  { id: 'assetId', control: 'AssetDropdownSingle', required: true, assetType, label: assetLabel },
  {
    id: 'date',
    control: 'CustomSingleSelect',
    options: DateOptions.map((o) => ({ ...o, isDefault: o.value === 'today' })),
  },
  {
    id: 'from',
    label: 'Date',
    control: 'DateSelect',
    dependsOn: 'date',
    dependsOnValue: 'custom',
    showTime: true,
  },
  {
    id: 'periodHours',
    control: 'CustomSingleSelect',
    options: PERIOD_OPTIONS.map((o) => ({
      ...o,
      isDefault: o.value === '24',
    })),
  },
]

export const isHistoryGraphValid = (configuration: HistoryGraphParams) => {
  if (configuration?.companyId && configuration.assetId) {
    return true
  }
  return 'Company and Asset are required.'
}

type HistoryGraphInsight = InsightDefinition<HistoryGraphParams, AssetHistory>

interface HistoryGraphDisplayOptions {
  flattenZoneLegend?: boolean
}

export const createHistoryGraphDisplay = ({
  flattenZoneLegend = false,
}: HistoryGraphDisplayOptions = {}): HistoryGraphInsight['Display'] =>
  function HistoryGraphDisplay({ status, data, configuration }) {
    const units = (useDashboard().configuration.units as TempUnits) ?? 'Metric'
    const params = configuration.parameters as HistoryGraphParams
    const dataFrom = !data || isInsightStatus(data) ? undefined : data.from
    // Stabilize derived values so parent re-renders don't churn AssetHistoryChart's effects.
    const from = React.useMemo(() => (dataFrom ? DateTime.fromISO(dataFrom) : undefined), [dataFrom])
    const periodHours = parseInt(params.periodHours || '24', 10) || 24
    const reload = React.useCallback(
      async (api: ApiService, window: HistoryWindow): Promise<AssetHistory | undefined> => {
        // Emit a zoneless wall-clock literal (no offset/Z) — same shape as the
        // picker's value — so the server applies the profile timezone consistently
        // and computeWindowStart never has to re-zone an instant.
        const windowStart = window.from.setZone(getUserTimezone()).toFormat("yyyy-MM-dd'T'HH:mm:ss")
        const response = await load(api, {
          ...params,
          date: 'custom',
          from: windowStart,
          periodHours: String(window.to.diff(window.from, 'hours').hours),
        })
        if (isInsightStatus(response)) {
          return undefined
        }
        return response
      },
      [params]
    )

    if (status.code === InsightStatusCode.NOT_CONFIGURED) {
      return <Alert type="info" message={status.message || 'Insight has not been configured'} />
    }
    // Render whenever we have data, including during a LOADING refresh — keeps the
    // chart mounted across status transitions so it doesn't blank on parent re-renders.
    if (!data || isInsightStatus(data) || !from) {
      return null
    }
    return (
      <section className="h-full w-full">
        <AssetHistoryChart
          data={data}
          from={from}
          periodHours={periodHours}
          reload={reload}
          units={units}
          temperatureLimits={data.temperatureLimits}
          flattenZoneLegend={flattenZoneLegend}
          externalLoading={status.code === InsightStatusCode.LOADING}
        />
      </section>
    )
  }

export const HistoryGraphDisplay = createHistoryGraphDisplay()

export const HistoryGraphCaption: NonNullable<HistoryGraphInsight['Caption']> = ({ configuration, status }) => {
  const [asset, setAsset] = React.useState<Asset>()
  const { companies } = useCompany()
  const { api } = useApi()
  const params = configuration.parameters as HistoryGraphParams
  React.useEffect(() => {
    if (!api) {
      return
    }
    const fn = async () => {
      if (params.assetId) {
        const response = await api.get<Asset[]>({
          path: `/assets?companyId=${params.companyId}&id=${params.assetId}&deleted=false${dataSourceParam(params)}`,
          origin: 'HistoryGraph#caption',
        })
        if (response.data?.length) {
          setAsset(response.data[0])
        } else {
          setAsset({ id: Number(params.assetId) } as Asset)
        }
      }
    }
    fn()
  }, [params])

  if (status.code === InsightStatusCode.ERROR) {
    return ''
  }
  const fields: Record<string, string> = {}
  if (params.companyId) {
    const company = companies.find((c) => c.id === params.companyId)
    if (company) {
      fields['Company'] = company.name
    }
  }
  if (asset) {
    fields['Asset'] = asset.name
  }
  // finite:false → for a custom date show just the start date; the period (not
  // an end date) defines the window, so "<date> - current" would be misleading.
  addDateToCaptionFields({ ...params, fields, finite: false })
  if (Object.keys(fields).length) {
    return <Caption fields={fields} />
  }
  return ''
}

interface HistoryGraphFooterOptions {
  showZoneCount?: boolean
}

export const createHistoryGraphFooter = ({ showZoneCount = true }: HistoryGraphFooterOptions = {}): NonNullable<
  HistoryGraphInsight['Footer']
> =>
  function HistoryGraphFooter({ data, status }) {
    if (status.code === InsightStatusCode.LOADING || status.code === InsightStatusCode.ERROR) {
      return ''
    }
    if (status.code === InsightStatusCode.NO_DATA || !data) {
      return <div>No data found</div>
    }

    const sampleCount = (data.ambient?.length ?? 0) + (data.movement?.length ?? 0)
    const samples = `${sampleCount} sample${sampleCount === 1 ? '' : 's'}`

    if (!showZoneCount) {
      return
    }
    const zoneCount = Object.keys(data.zones ?? {}).length
    return (
      <div>
        {zoneCount} zone{zoneCount === 1 ? '' : 's'}, {samples}
      </div>
    )
  }

export const HistoryGraphFooter = createHistoryGraphFooter()

const HistoryGraph: HistoryGraphInsight = {
  active: true,
  slug: 'asset/history-graph',
  category: 'asset',
  name: 'Temperature Graph',
  description: 'Display asset temperature graph for a given date range',
  defaultWidth: 'full',
  defaultHeight: { lg: 550, md: 550, sm: 550 },
  defaultAspectRatio: 16 / 9,

  configuration: {
    controls: buildHistoryGraphControls(AssetTypeFilter.InTransit),
    isValid: isHistoryGraphValid,
  },

  load,

  Display: HistoryGraphDisplay,

  Caption: HistoryGraphCaption,

  Footer: HistoryGraphFooter,
}

export default HistoryGraph
